import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useContacts from "../../../services/chat/useContacts";
import useChats from "../../../services/chat/useChats";
import useMedia from "../../../services/chat/useMedia";
import useTemplates from "../../../services/Whatsapp/useTemplates";

import ContactAdd from "./ContactAdd";
import ChatWindow from "./ChatWindow";
import MessageInput from "./MessageInput";
import MediaLibraryModal from "./MediaLibraryModal";
import TemplateModal from "./TemplateModal";
import SendTemplateModal from "./SendTemplateModal";

import wpImage from "../../../images/Whatsapp.svg";
import whatsappBg from "../../../images/chat_BG.png";
import EditContactModal from "./EditContactModal";
import useWhatsappNumbers from "../../../services/Whatsapp/useWhatsappNumbers";
import notificationSound from "../../../Sound/messageTone.mp3";
import { Pencil, UserCheck } from "lucide-react";

/* ─── tokens ─── */
const C = {
  green: "#25d366",
  greenDk: "#128c7e",
  bg: "#ffffff",
  white: "#ffffff",
  border: "#d1d5db",
  text: "#111b21",
  sub: "#667781",
  muted: "#8696a0",
  red: "#e53e3e",
};

/* ─── delete confirm modal ─── */
const DeleteModal = ({ contact, onConfirm, onCancel }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(0,0,0,0.45)",
    }}
    onClick={onCancel}
  >
    <div
      style={{
        background: C.white,
        borderRadius: 14,
        padding: "28px 32px",
        width: 340,
        boxShadow: "0 8px 32px rgba(0,0,0,.18)",
        textAlign: "center",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* icon */}
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: "#fff0f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px",
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={C.red}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14H6L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
          <path d="M9 6V4h6v2" />
        </svg>
      </div>
      <p
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: C.text,
          margin: "0 0 8px",
        }}
      >
        Are you sure?
      </p>
      <p
        style={{
          fontSize: 13.5,
          color: C.sub,
          margin: "0 0 24px",
          lineHeight: 1.5,
        }}
      >
        Do you want to delete{" "}
        <strong>{contact?.contact_name || contact?.contact_number}</strong>?
        This action cannot be undone.
      </p>
      <div style={{ display: "flex", gap: 10 }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: "10px 0",
            borderRadius: 8,
            border: `1px solid ${C.border}`,
            background: C.white,
            color: C.sub,
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          style={{
            flex: 1,
            padding: "10px 0",
            borderRadius: 8,
            border: "none",
            background: C.red,
            color: C.white,
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(229,62,62,.3)",
          }}
        >
          Confirm
        </button>
      </div>
    </div>
  </div>
);

const Chats = ({ userID }) => {
  const {
    contacts,
    chatContacts,
    fetchChatContacts,
    searchChatContacts,
    deleteContact,
    fetchContacts,
    loading,
  } = useContacts();

  const {
    chats,
    people,
    labels,
    loadingChats,
    fetchChats,
    sendMessage,
    getAllUserList,
    fetchLabels,
    editContact,
    assignLabelToChat,
    assignTeamMemberToChat,
    fetchChatContact,
  } = useChats();

  const { mediaList, fetchUploadedMedia, sendMedia, loadingMedia } =
    useMedia(userID);

  const {
    templates,
    fetchVendorTemplates,
    deleteVendorTemplate,
    fetchTemplates,
    executeTemplate,
  } = useTemplates(userID);

  const { whatsappNumbers } = useWhatsappNumbers();

  const [activeTab, setActiveTab] = useState("chats");
  const [prevTab, setPrevTab] = useState("chats"); // track previous tab for "add"
  const [search, setSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [mediaType, setMediaType] = useState("");
  const [chatHasMore, setChatHasMore] = useState(true);

  /* delete modal */
  const [deleteTarget, setDeleteTarget] = useState(null); // contact to delete
  const [deleting, setDeleting] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [selectedPerson, setSelectedPerson] = useState("");
  const [SingleWhatsApp, setSingleWhatsApp] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSendTemplateModal, setShowSendTemplateModal] = useState(false);
  const [contactPage, setContactPage] = useState(1);
  const [contactHasMore, setContactHasMore] = useState(true);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const isFetchingContacts = useRef(false);
  const contactPageRef = useRef(1);
  const audioRef = useRef(null);
  const searchRef = useRef(null);

  // delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 600);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (activeTab === "add") return;

    if (!debouncedSearch.trim()) return;

    if (debouncedSearch.length < 3) return;

    searchChatContacts(debouncedSearch.trim());
  }, [debouncedSearch, activeTab]);

  const fetchMoreContacts = async () => {
    if (loading || !contactHasMore || isFetchingContacts.current) return;

    isFetchingContacts.current = true;

    const nextPage = contactPageRef.current + 1;

    const res = await fetchChatContacts(nextPage);

    if (!res?.data || res.data.length === 0) {
      setContactHasMore(false);
    } else {
      contactPageRef.current = nextPage;
      setContactPage(nextPage);
    }

    isFetchingContacts.current = false;
  };

  const [chatPage, setChatPage] = useState(1);

  const fetchMoreChats = async () => {
    if (!selectedContact || loadingChats || !chatHasMore) return;

    const nextPage = chatPage + 1;

    const res = await fetchChats(selectedContact.contact_number, nextPage);

    if (!res?.data || res.data.length === 0) {
      setChatHasMore(false);
      return;
    }

    setChatPage(nextPage);
  };

  const handleAssign = async (personId) => {
    if (!selectedContact || !personId) return;

    const res = await assignTeamMemberToChat(selectedContact, personId);

    if (res?.responseCode === "00") {
      toast.success(res.responseMessage || "Chat assigned successfully");

      // reload contact list
      fetchChatContacts();

      // reset dropdown
      setSelectedPerson("");
    } else {
      toast.error(res?.responseMessage || "Assign failed");
    }
  };

  const handleAddLabel = async (labelTitle) => {
    if (!selectedContact || !labelTitle) return;

    const res = await assignLabelToChat(selectedContact, labelTitle);

    if (res?.responseCode === "00") {
      toast.success(res.responseMessage);

      fetchChatContacts();

      setSelectedLabel("");
    } else {
      toast.error(res?.responseMessage || "Label assign failed");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];

      const payload = {
        mobile: selectedContact?.contact_number,
        mediaData: base64,
        mediaType: file.type.startsWith("image")
          ? "img"
          : file.type.startsWith("video")
            ? "video"
            : "pdf",
        message: "",
      };

      try {
        const res = await sendMedia(payload);

        if (res?.responseCode === "00") {
          toast.success("Media sent successfully ");
        } else {
          toast.error(res?.responseMessage || "Failed to send media ");
        }
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong ");
      }
    };

    reader.readAsDataURL(file);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearch("");

    if (tab !== "add") {
      setContactPage(1);
      contactPageRef.current = 1;
      setContactHasMore(true);
    }

    if (tab === "add") {
      setSelectedContact(null);
    }
  };

  const lastMsgRef = useRef("");

  useEffect(() => {
    if (!userID || !selectedContact?.contact_number) return;

    const socket = new WebSocket(
      `wss://payinnew.remittpe.com/ws?userId=${userID}&roomId=${selectedContact.contact_number}`,
    );

    socket.onopen = () => {
      console.log("Chat WebSocket Connected:", selectedContact.contact_number);
    };

    socket.onmessage = (event) => {
      try {
        if (lastMsgRef.current === event.data) return;
        lastMsgRef.current = event.data;

        const message = JSON.parse(event.data);
        fetchChats(selectedContact.contact_number, 1);

        audioRef.current?.play().catch(() => {});
      } catch (error) {
        console.error("Invalid WS message:", event.data);
      }
    };

    return () => socket.close();
  }, [selectedContact?.contact_number, userID]);

  useEffect(() => {
    getAllUserList();
    fetchLabels();
  }, []);

  /* ── contact click ── */
  const handleSelectContact = (contact) => {
    if (activeTab === "add") return;

    setSelectedContact(contact);
    setChatHasMore(true);
    setChatPage(1);

    fetchChats(contact.contact_number, 1);
  };

  const handleSendMessage = async (msg) => {
    if (!selectedContact) return;
    await sendMessage(selectedContact.contact_number, msg);
    fetchChats(selectedContact.contact_number);
  };

  /* ── delete ── */
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const data = await deleteContact(deleteTarget.contact_number);
      if (data?.responseCode === "00") {
        toast.success(data?.responseMessage || "Contact deleted successfully");
        fetchChatContacts();
      } else {
        toast.error(data?.responseMessage || "Delete failed");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  /* ── data for each tab ── */
  const filterFn = (list) => {
    const q = search.toLowerCase();
    if (!q) return list || [];
    return (list || []).filter(
      (c) =>
        (c.contact_name || "").toLowerCase().includes(q) ||
        (c.contact_number || "").includes(q),
    );
  };

  useEffect(() => {
    fetchChatContacts();
  }, []);

  const chatFiltered = filterFn(chatContacts);

  const contactFiltered = chatFiltered;
  const addFiltered = chatFiltered;

  const chatPaged = chatFiltered;
  const contactPaged = contactFiltered;
  const addPaged = addFiltered;

  const formatTime = (dt) => {
    if (!dt) return "";

    return new Date(dt).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  /* ── shared row styles ── */
  const rowStyle = (selected) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderBottom: `1px solid ${C.border}`,
    cursor: "pointer",
    background: selected ? "#f0fdf4" : C.white,
    borderLeft: selected ? `3px solid ${C.green}` : "3px solid transparent",
    transition: "background .1s",
  });

  useEffect(() => {
    if (!SingleWhatsApp) return;
    fetchVendorTemplates(SingleWhatsApp);
  }, [SingleWhatsApp]);

  useEffect(() => {
    const audio = new Audio(notificationSound);
    audio.preload = "auto";
    audio.loop = false;
    audio.volume = 1.0;

    audioRef.current = audio;

    const unlockAudio = () => {
      audio.muted = true;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.muted = false;
          localStorage.setItem("soundUnlocked", "true");
        })
        .catch(() => {});

      document.removeEventListener("click", unlockAudio);
    };

    if (localStorage.getItem("soundUnlocked") !== "true") {
      document.addEventListener("click", unlockAudio, { once: true });
    }

    return () => {
      document.removeEventListener("click", unlockAudio);
    };
  }, []);

  const lastGlobalMsg = useRef("");

  useEffect(() => {
    if (!userID) return;

    const socket = new WebSocket(
      `wss://payinnew.remittpe.com/ws?userId=${userID}&roomId=new_conversation`,
    );

    let isConnected = false;

    socket.onopen = () => {
      isConnected = true;
    };

    socket.onmessage = (event) => {
      try {
        if (!isConnected) return;
        if (lastGlobalMsg.current === event.data) return;
        lastGlobalMsg.current = event.data;

        JSON.parse(event.data);

        if (activeTab === "chats") {
          fetchChatContacts();
        }

        audioRef.current?.play().catch(() => {});
      } catch (error) {
        console.error("Invalid JSON message:", event.data);
      }
    };

    return () => socket.close();
  }, [userID, activeTab]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  return (
    <>
      <ToastContainer />

      <div
        className="montserrat-font-1"
        style={{
          display: "flex",
          height: "100%",
          width: "100%",
          overflow: "hidden",
        }}
      >
        {/* ══════════ LEFT PANEL ══════════ */}
        <div
          style={{
            width: 480,
            minWidth: 290,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            background: C.white,
            borderRight: `1px solid ${C.border}`,
            // overflow: "hidden",
          }}
        >
          {/* ── Tabs ── */}
          <div
            style={{
              display: "flex",
              borderBottom: `1px solid ${C.border}`,
              flexShrink: 0,
            }}
          >
            {[
              ["chats", "Chats"],
              ["contacts", "Contacts"],
              ["add", "+ Add"],
            ].map(([v, l]) => (
              <button
                key={v}
                onClick={() => handleTabChange(v)}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  fontSize: 13.5,
                  fontWeight: 500,
                  background: "none",
                  border: "none",
                  borderBottom:
                    activeTab === v
                      ? `2.5px solid ${C.green}`
                      : "2.5px solid transparent",
                  color: activeTab === v ? C.green : C.sub,
                  cursor: "pointer",
                }}
              >
                {l}
              </button>
            ))}
          </div>

          {/* ── Search ── */}
          <div
            style={{
              padding: "8px 10px",
              borderBottom: `1px solid ${C.border}`,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: C.bg,
                borderRadius: 8,
                padding: "6px 10px",
              }}
            >
              <form autoComplete="off montserrat-font-1">
                <input
                  ref={searchRef}
                  type="text"
                  name="chatSearch"
                  autoComplete="off"
                  placeholder="Search by name or mobile..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    border: "none",
                    background: "transparent",
                    outline: "none",
                    fontSize: 12,
                    color: C.text,
                    width: "100%",
                  }}
                />
              </form>
            </div>
          </div>

          {/* ══ CHATS TAB ══ */}
          {activeTab === "chats" && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "9px 14px 5px",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: C.sub,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Recent Chats
                </span>
                <button
                  onClick={() => {
                    const phoneId =
                      SingleWhatsApp ||
                      (whatsappNumbers?.length > 0
                        ? whatsappNumbers[0].phone_no_id
                        : "");

                    if (!phoneId) {
                      console.error(" No WhatsApp number found");
                      return;
                    }

                    setSingleWhatsApp(phoneId);
                    fetchVendorTemplates(phoneId);

                    setShowSendTemplateModal(true);
                  }}
                  className="text-blue-600 underline font-medium hover:text-blue-700"
                >
                  Send New
                </button>
              </div>

              <div
                style={{ flex: 1, overflowY: "auto" }}
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.target;

                  if (scrollHeight - scrollTop <= clientHeight + 5) {
                    fetchMoreContacts();
                  }
                }}
              >
                {chatPaged.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => handleSelectContact(c)}
                    style={rowStyle(selectedContact?.id === c.id)}
                    onMouseEnter={(e) => {
                      if (selectedContact?.id !== c.id)
                        e.currentTarget.style.background = "#f0fdf4";
                    }}
                    onMouseLeave={(e) => {
                      if (selectedContact?.id !== c.id)
                        e.currentTarget.style.background = C.white;
                    }}
                  >
                    <img
                      src={wpImage}
                      alt=""
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        flexShrink: 0,
                      }}
                    />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* NAME + NUMBER + ASSIGNED ICON */}
                      <div
                        style={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: C.text,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span>{c.contact_name || "Unknown"}</span>

                        <span style={{ color: C.sub }}>
                          ({c.contact_number})
                        </span>

                        {/* Assigned user icon */}
                        {c.assigned_user && c.assigned_user !== "NA" && (
                          <span
                            title={c.assigned_user.split("|")[0]}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                              background: "#e6f7ff",
                              border: "1px solid #1877f2",
                              marginLeft: 4,
                            }}
                          >
                            <UserCheck
                              className="w-[14px] h-[14px] text-blue-600 opacity-90"
                              strokeWidth={2.5}
                            />
                          </span>
                        )}
                      </div>

                      {/* LAST MESSAGE */}
                      <div
                        style={{
                          fontSize: 12,
                          color: C.sub,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          marginTop: 3,
                        }}
                      >
                        {c.last_conversation || "No messages yet"}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        minHeight: 40,
                      }}
                    >
                      {/* TOP RIGHT → LABEL */}
                      {c.label?.title && (
                        <span
                          style={{
                            fontSize: 10,
                            padding: "2px 6px",
                            borderRadius: 6,
                            fontWeight: 600,
                            background:
                              c.label.title.toLowerCase() === "lead"
                                ? "#fff4e5"
                                : c.label.title.toLowerCase() === "client" ||
                                    c.label.title.toLowerCase() === "clients"
                                  ? "#e6f7ff"
                                  : c.label.title.toLowerCase() === "promotions"
                                    ? "#f3e8ff"
                                    : "#f1f5f9",
                            color:
                              c.label.title.toLowerCase() === "lead"
                                ? "#d97706"
                                : c.label.title.toLowerCase() === "client" ||
                                    c.label.title.toLowerCase() === "clients"
                                  ? "#1877f2"
                                  : c.label.title.toLowerCase() === "promotions"
                                    ? "#7c3aed"
                                    : "#475569",
                          }}
                        >
                          {c.label.title.toUpperCase()}
                        </span>
                      )}

                      {/* BOTTOM RIGHT → TIME + UNREAD */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginTop: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            color: C.muted,
                          }}
                        >
                          {formatTime(c.indate)}
                        </span>

                        {Number(c.unread_count) > 0 && (
                          <span
                            style={{
                              minWidth: 20,
                              height: 20,
                              borderRadius: 10,
                              background: C.green,
                              color: C.white,
                              fontSize: 11,
                              fontWeight: 700,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "0 6px",
                            }}
                          >
                            {c.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {chatFiltered.length === 0 && !loading && (
                  <div
                    style={{
                      padding: 32,
                      textAlign: "center",
                      color: C.muted,
                      fontSize: 13,
                    }}
                  >
                    No chats found
                  </div>
                )}
              </div>
              {loading && (
                <div style={{ padding: 12, textAlign: "center" }}>
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      border: "3px solid #25d366",
                      borderTop: "3px solid transparent",
                      borderRadius: "50%",
                      margin: "0 auto",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/* ══ CONTACTS TAB ══ */}
          {activeTab === "contacts" && (
            <>
              <div style={{ padding: "10px 14px 6px", flexShrink: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  All Contacts
                </span>
              </div>

              <div
                style={{ flex: 1, overflowY: "auto" }}
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.target;

                  if (scrollHeight - scrollTop <= clientHeight + 5) {
                    fetchMoreContacts();
                  }
                }}
              >
                {loading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "40px 0",
                    }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        border: "3px solid #e5e7eb",
                        borderTop: "3px solid #25d366",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                      }}
                    />
                  </div>
                ) : (
                  contactPaged.map((c) => (
                    <div
                      key={c.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 14px",
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      <img
                        src={wpImage}
                        alt=""
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13.5,
                            fontWeight: 500,
                            color: C.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {c.contact_name || "Unknown"}
                        </div>
                        <div style={{ fontSize: 12, color: C.sub }}>
                          {c.contact_number}
                        </div>
                      </div>
                      {/* Delete btn */}
                      <button
                        onClick={() => setDeleteTarget(c)}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          border: `1px solid ${C.red}`,
                          background: "#ffffff",
                          color: C.red,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          transition: "all .12s",
                        }}
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14H6L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M9 6V4h6v2" />
                        </svg>
                      </button>
                    </div>
                  ))
                )}

                {contactFiltered.length === 0 && !loading && (
                  <div
                    style={{
                      padding: 32,
                      textAlign: "center",
                      color: C.muted,
                      fontSize: 13,
                    }}
                  >
                    No contacts found
                  </div>
                )}
              </div>
            </>
          )}

          {/* ADD TAB */}
          {activeTab === "add" && (
            <>
              <div style={{ padding: "10px 14px 6px", flexShrink: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  {prevTab === "contacts" ? "All Contacts" : "Recent Chats"}
                </span>
              </div>

              <div
                style={{ flex: 1, overflowY: "auto" }}
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.target;

                  if (scrollHeight - scrollTop <= clientHeight + 5) {
                    fetchMoreContacts();
                  }
                }}
              >
                {loading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "40px 0",
                    }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        border: "3px solid #e5e7eb",
                        borderTop: "3px solid #25d366",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                      }}
                    />
                  </div>
                ) : (
                  addPaged.map((c) => (
                    <div
                      key={c.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 14px",
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      <img
                        src={wpImage}
                        alt=""
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                        }}
                      />

                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 500 }}>
                          {c.contact_name || "Unknown"}
                        </div>

                        <div style={{ fontSize: 12, color: "#667781" }}>
                          {c.contact_number}
                        </div>
                      </div>
                    </div>
                  ))
                )}

                {addFiltered.length === 0 && !loading && (
                  <div
                    style={{
                      padding: 32,
                      textAlign: "center",
                      color: C.muted,
                      fontSize: 13,
                    }}
                  >
                    No data found
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ══════════ RIGHT PANEL ══════════ */}
        <div
          style={{
            flex: 1,
            position: "relative",
            overflow: "hidden",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* WA background always behind */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 0,
              backgroundImage: `url(${whatsappBg})`,
              backgroundPosition: "center",
              backgroundColor: "#ffffff",
            }}
          />

          {/* No contact selected OR add tab → ContactAdd form */}
          {(!selectedContact || activeTab === "add") && (
            <div
              style={{
                position: "absolute",
                flex: 1,
                inset: 0,
                // width: "100%",
                height: "100%",
                zIndex: 5,
                backgroundColor: "#ffffff",
                borderLeft: `1px solid ${C.border}`,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ContactAdd userID={userID} />
            </div>
          )}

          {/* Contact selected (only in chats/contacts tab) → chat window */}
          {selectedContact && activeTab !== "add" && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 5,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Chat Header */}
              <div
                style={{
                  height: 60,
                  background: "#19b77a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0 18px",
                  color: "#fff",
                  fontWeight: 600,
                }}
              >
                {/* Contact Name */}
                <div style={{ fontSize: 15 }}>
                  {selectedContact.contact_name} (
                  {selectedContact.contact_number})
                </div>

                {/* Right controls */}
                <div style={{ display: "flex", gap: 10 }}>
                  <select
                    value={selectedPerson}
                    onChange={(e) => {
                      const personId = e.target.value;
                      setSelectedPerson(personId);
                      handleAssign(personId);
                    }}
                    style={{
                      height: 32,
                      minWidth: 200,
                      borderRadius: 6,
                      border: "none",
                      padding: "0 10px",
                      color: "#111827",
                      fontWeight: 500,
                    }}
                  >
                    <option value="">Assign Chat</option>

                    {people?.map((user) => (
                      <option key={user.userid} value={user.userid}>
                        {user.user_name}
                      </option>
                    ))}
                  </select>

                  <select
                    value={selectedLabel}
                    onChange={(e) => {
                      const labelIndex = e.target.value;
                      setSelectedLabel(labelIndex);
                      handleAddLabel(labelIndex);
                    }}
                    style={{
                      height: 32,
                      borderRadius: 6,
                      border: "none",
                      padding: "0 10px",
                      background: "#fff",
                      color: "#111827",
                      cursor: "pointer",
                    }}
                  >
                    <option value="">Add Label</option>

                    {labels?.map((label, index) => (
                      <option key={index} value={index}>
                        {label.title}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() => setShowEditModal(true)}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      border: "none",
                      background: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <Pencil
                      className="w-[1.8rem] h-[1.8rem] text-gray-700 cursor-pointer hover:text-blue-500 transition-colors"
                      strokeWidth={2}
                    />
                  </button>
                </div>
              </div>

              {/* Chat Messages */}
              <div
                className="chat-messages-scroll"
                style={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <ChatWindow
                  chats={chats}
                  isLoadingChats={loadingChats}
                  chatHasMore={chatHasMore}
                  fetchMoreChats={fetchMoreChats}
                />
              </div>

              <MessageInput
                handleSendMessage={handleSendMessage}
                handleFileSelect={handleFileSelect}
                setMediaType={setMediaType}
                openMediaLibrary={() => {
                  fetchUploadedMedia();
                  setShowMediaModal(true);
                }}
                openTemplateModal={() => {
                  const phoneId =
                    SingleWhatsApp || whatsappNumbers?.[0]?.phone_no_id || "";
                  setSingleWhatsApp(phoneId);
                  fetchVendorTemplates(SingleWhatsApp);
                  setShowTemplateModal(true);
                }}
              />
            </div>
          )}
        </div>

        {/* ══ DELETE CONFIRM MODAL ══ */}
        {deleteTarget && (
          <DeleteModal
            contact={deleteTarget}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={handleDeleteConfirm}
          />
        )}

        {/* Modals */}
        <MediaLibraryModal
          show={showMediaModal}
          onClose={() => setShowMediaModal(false)}
          mediaList={mediaList}
          loadingMedia={loadingMedia}
          fetchUploadedMedia={fetchUploadedMedia}
        />

        <TemplateModal
          show={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          templates={templates}
          executeTemplate={executeTemplate}
          WhatsappNumbers={whatsappNumbers}
          SingleWhatsApp={SingleWhatsApp}
          setSingleWhatsApp={setSingleWhatsApp}
          fetchVendorTemplates={fetchVendorTemplates}
          selectedContact={selectedContact}
        />

        <EditContactModal
          show={showEditModal}
          contact={selectedContact}
          onClose={() => setShowEditModal(false)}
          onSubmit={async (data) => {
            const res = await editContact(data);

            if (res?.responseCode === "00") {
              toast.success("Contact Edited Successfully");
              setShowEditModal(false);
            }
          }}
        />

        <SendTemplateModal
          show={showSendTemplateModal}
          onClose={() => setShowSendTemplateModal(false)}
          whatsappNumbers={whatsappNumbers}
          SingleWhatsApp={SingleWhatsApp}
          setSingleWhatsApp={setSingleWhatsApp}
          templates={templates}
          fetchVendorTemplates={fetchVendorTemplates}
          executeTemplate={executeTemplate}
          userID={userID}
        />
      </div>
    </>
  );
};

export default Chats;
